import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, Select } from "antd";
import { ExportToExcel } from "../../../../utils";

const { Option } = Select;

export const ChartChannelComparative = ({ data }) => {
  const [selectedChannel, setSelectedChannel] = useState("TODOS");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = [
    "#0a9396",
    "#001219",
    "#94d2bd",
    "#e9d8a6",
    "#f8ad9d",
    "#ee9b00",
    "#ca6702",
    "#ae2012",
  ];

  const transformedData = useMemo(() => {
    const dataByDate = {};

    const channelsSet = new Set();
    data?.forEach((item) => channelsSet?.add(item.channel));
    const channels = [...channelsSet];

    data?.forEach((item) => {
      const date = item.dateStart.toDate().toLocaleDateString();

      if (!dataByDate[date]) {
        dataByDate[date] = {};
        channels?.forEach((channel) => {
          dataByDate[date][`${channel}`] = 0;
          dataByDate[date][`${channel}_ordenes`] = 0;
          dataByDate[date][`${channel}_productos`] = 0;
          dataByDate[date][`${channel}_total`] = 0;
          dataByDate[date][`${channel}_entregado`] = 0;
        });
      }

      const channel = item.channel;
      const amount = Number(item.saleAmount?.toFixed(2));
      const productQuantity = item.products?.reduce(
        (sum, product) => sum + product.quantity,
        0
      );

      dataByDate[date][`${channel}`] += amount;
      dataByDate[date][`${channel}_ordenes`] += 1;
      dataByDate[date][`${channel}_productos`] += productQuantity;
      dataByDate[date][`${channel}_total`] += amount;
      if (item.status === "Entregado") {
        dataByDate[date][`${channel}_entregado`] += amount;
      }
    });

    return Object.keys(dataByDate)?.map((date) => ({
      date,
      ...dataByDate[date],
    }));
  }, [data]);

  const channels = useMemo(() => {
    const channelsSet = new Set();
    data?.forEach((item) => channelsSet?.add(item.channel));
    return [...channelsSet];
  }, [data]);

  const lines = useMemo(() => {
    const channelsToDisplay =
      selectedChannel && selectedChannel !== "TODOS"
        ? [selectedChannel]
        : channels;
    return channelsToDisplay?.map((channel, index) => (
      <Line
        type="monotone"
        dataKey={channel}
        stroke={colors[index % colors.length]}
        key={index}
        strokeWidth={2}
        dot={false}
      ></Line>
    ));
  }, [channels, colors, selectedChannel]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const channelData = payload?.find((item) => item.name === selectedChannel);

      return (
        <div className="custom-tooltip">
          <b className="label">{`${label}`}</b>
          {payload?.map((item, index) => (
            <p key={index} className="desc" style={{ color: item.color }}>
              {selectedChannel === item.name
                ? ""
                : `${item.name} : S/ ${item.value?.toFixed(2)}`}
            </p>
          ))}
          {channelData && (
            <>
              <p className="desc">
                Total: S/{" "}
                {channelData.payload[`${selectedChannel}_total`].toFixed(2)}
              </p>
              <p className="desc">
                Total Entregado: S/{" "}
                {channelData.payload[`${selectedChannel}_entregado`].toFixed(2)}
              </p>
              <p className="desc">
                Total de Órdenes:{" "}
                {channelData.payload[`${selectedChannel}_ordenes`]}
              </p>
              <p className="desc">
                Total de Productos:{" "}
                {channelData.payload[`${selectedChannel}_productos`]}
              </p>
              <p className="desc">
                Ticket Promedio:{" "}
                {(
                  channelData.payload[`${selectedChannel}_productos`] /
                  channelData.payload[`${selectedChannel}_ordenes`]
                ).toFixed(2)}
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const exportData = transformedData?.map((item) => {
    const row = { Fecha: item.date };
    channels?.forEach((channel) => {
      row[`${channel}`] = `S/ ${item[`${channel}`]?.toFixed(2)}`;
      row[`${channel}_ordenes`] = item[`${channel}_ordenes`];
      row[`${channel}_productos`] = item[`${channel}_productos`];
      row[`${channel}_ticketPromedio`] =
        item[`${channel}_ordenes`] > 0
          ? (item[`${channel}_productos`] / item[`${channel}_ordenes`])?.toFixed(
            2
          )
          : 0;
    });
    return row;
  });

  const exportColumns = [
    { title: "Fecha", dataIndex: "Fecha", key: "Fecha", width: 200 },
    ...channels?.flatMap((channel) => [
      {
        title: `${channel}`,
        dataIndex: `${channel}`,
        key: `${channel}`,
        width: 200,
      },
      {
        title: `${channel}_ordenes`,
        dataIndex: `${channel}_ordenes`,
        key: `${channel}_ordenes`,
        width: 200,
      },
      {
        title: `${channel}_productos`,
        dataIndex: `${channel}_productos`,
        key: `${channel}_productos`,
        width: 200,
      },
      {
        title: `${channel}_ticketPromedio`,
        dataIndex: `${channel}_ticketPromedio`,
        key: `${channel}_ticketPromedio`,
        width: 200,
      },
    ]),
  ];

  return (
    <Card
      title={
        <div className="flex justify-center gap-1 lg:justify-between" >
          Comparativa de Ventas por Canal
          <Select
            className="w-52"
            defaultValue="TODOS"
            onChange={(value) =>
              setSelectedChannel(value === "TODOS" ? null : value)
            }
          >
            <Option value="TODOS">TODOS</Option>
            {channels?.map((channel, index) => (
              <Option key={index} value={channel}>
                {channel}
              </Option>
            ))}
          </Select>
        </div>
      }
      bordered={true}
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={transformedData}
          margin={{
            top: 10,
            right: 20,
            left: 0,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {lines}
        </LineChart>
      </ResponsiveContainer>
      <style jsx>{`
        .custom-tooltip {
          background-color: white;
          border: 1px solid #ccc;
          padding: 10px;
          font-size: 14px;
        }
      `}</style>
      <div
        className="flex justify-center mt-2 mb-5"
      >
        <ExportToExcel
          data={exportData}
          columns={exportColumns}
          fileName="comparativaPorCanal"
        />
      </div>
    </Card>
  );
};
